import StudySessionFeedbackContainer from "@features/session/containers/StudySessionFeedbackContainer";
import { useAppStore } from "@hooks/useAppStore";
import type { BaseItemSchema } from "@memorang/types";
import HighlightCorrectSummary from "@memorang/ui/components/StudySession/SessionContent/item-types/HighlightCorrectSummary";
import MultipleChoiceMultipleAnswerListening from "@memorang/ui/components/StudySession/SessionContent/item-types/MultipleChoiceMultipleAnswerListening";
import MultipleChoiceSingleAnswerListening from "@memorang/ui/components/StudySession/SessionContent/item-types/MultipleChoiceSingleAnswerListening";
import SelectMissingWord from "@memorang/ui/components/StudySession/SessionContent/item-types/SelectMissingWord";
import { useDevice } from "@memorang/ui/useDevice";
import { useCallback } from "react";
import FormativeTimeOverrunContainer from "../../../containers/FormativeTimeOverrunContainer";
import HintContainer from "../../../containers/HintContainer";
import { extractAudioUri, extractVideoUri } from "../../../helpers/content";
import {
	getAllowAudioReplay,
	getAllowVideoReplay,
	getSummativeResponseConfig,
	getVideoPlayerConfig,
	getVideoProgressSeekable,
} from "../../../helpers/response-timing";
import useChoiceInteraction from "../../../hooks/useChoiceInteraction";
import useResponseControlsStore from "../../../hooks/useResponseControlsStore";

type Props = {
	item: BaseItemSchema;
	isSplitLayout: boolean;
};

const ChoiceContainer = ({ item, isSplitLayout }: Props) => {
	const { content, response } = item;
	const itemType = item.metadata?.type;

	const {
		choices,
		correctAnswerIds,
		numChoicesRequired,
		isAnswered,
		isSummativeTest,
		selectedChoiceIds,
		handleSelectChoice,
	} = useChoiceInteraction(item);

	const videoUri = extractVideoUri(content);
	const audioUri = extractAudioUri(content);
	const instruction =
		response.type === "OPTION_SELECTION" ? (response.instruction ?? "") : "";
	const shuffle =
		response.type === "OPTION_SELECTION" ? (response.shuffle ?? false) : false;
	const showTimerConfig = useAppStore((s) => Boolean(s.sessionView?.timer));
	const responseConfig = getSummativeResponseConfig(
		response,
		isSummativeTest || showTimerConfig,
	);
	const prepTime = responseConfig?.prepTime;
	const choiceAllowAudioReplay = getAllowAudioReplay(
		responseConfig,
		isSummativeTest,
	);
	const choiceAllowVideoReplay = getAllowVideoReplay(
		responseConfig,
		isSummativeTest,
	);
	const videoPlayerConfig = getVideoPlayerConfig(
		responseConfig,
		isSummativeTest,
	);
	const progressSeekable = getVideoProgressSeekable(isSummativeTest);
	const markAudioComplete = useResponseControlsStore(
		(s) => s.markAudioComplete,
	);
	const handleAudioEnded = useCallback(
		() => markAudioComplete(item.id!),
		[markAudioComplete, item.id],
	);

	const { isDesktopWeb } = useDevice();
	const itemStem = content.stem?.text ?? "";
	const renderFeedback = isDesktopWeb
		? () => <StudySessionFeedbackContainer isSplitLayout={isSplitLayout} />
		: undefined;

	const sharedProps = {
		audioUri,
		videoUri,
		instruction,
		shuffle,
		choices,
		itemId: item.id!,
		selectedChoiceIds,
		correctAnswerIds,
		numChoicesRequired,
		isAnswered,
		isSummativeTest,
		handleSelectChoice,
		prepTime,
		allowAudioReplay: choiceAllowAudioReplay,
		allowVideoReplay: choiceAllowVideoReplay,
		videoPlayerConfig,
		progressSeekable,
		onAudioEnded: handleAudioEnded,
		stem: itemStem,
		renderFeedback,
	};

	const renderHint =
		isAnswered || isSummativeTest
			? undefined
			: () => (
					<>
						<FormativeTimeOverrunContainer />
						<HintContainer key={item.id} item={item} />
					</>
				);

	if (itemType === "MCMAL" || itemType === "MCMAL_CORE") {
		return (
			<MultipleChoiceMultipleAnswerListening
				{...sharedProps}
				renderHint={renderHint}
			/>
		);
	}

	if (itemType === "SMWL" || itemType === "SMWL_CORE") {
		return (
			<>
				<SelectMissingWord {...sharedProps} />
				<FormativeTimeOverrunContainer />
			</>
		);
	}

	if (itemType === "MCSAL" || itemType === "MCSAL_CORE") {
		return (
			<MultipleChoiceSingleAnswerListening
				{...sharedProps}
				renderHint={renderHint}
			/>
		);
	}

	return (
		<>
			<HighlightCorrectSummary {...sharedProps} />
			<FormativeTimeOverrunContainer />
		</>
	);
};

export default ChoiceContainer;
